import { memo, useState } from "react";
import RomshInputTable from "@/components/dashboard/RomshInputTable";
import { SHOW_ROMSH_INPUTS } from "@/config/displayPolicy";
import { ROMSH_OOD_WARNING } from "@/constants/romshOod";
import type { Analysis, AnalysisEvidence, AnalysisFeature } from "@/types";
import type { FeatureData } from "@/types/spectrum";
import {
  deriveNaturalConclusion,
  type RomshDecision,
} from "@/utils/naturalConclusion";
import { panelCls } from "@/utils/panelCls";

/**
 * S23-E 경찰청 미팅 결정: "방화/자연발화" 단정 문구 전면 제거.
 * "방화 의심도" → "인화성 물질 패턴 유사도 (보조 연구지표)".
 * 원 확률 수치는 상세/툴팁에만 노출(거짓 정밀도 회피).
 */

const ZONE_TOOLTIP =
  "모델이 기록한 판정을 그대로 보여 줍니다. 모델은 확률이 자기 임계값(분석마다 함께 기록) 이상이면 '인화성 패턴 유사'로 기록합니다. 전문가 검토 권고. 보조 연구지표이며 방화 등 최종 판단은 수사기관·국과수 종합 영역입니다.";

const ROMSH_ABBR_TOOLTIP =
  "RoMSH: Retention-time-aware Mixture Sequence Handler — 인하대 트랜스포머 기반 인화성 물질 패턴 유사도 보조 연구지표. 방화 여부 최종 판단 모델이 아닙니다.";

const AI_AUX_CAVEAT =
  "보조 연구지표이며 판단·결론이 아닙니다. 최종 판단은 수사기관·국과수 종합 영역입니다.";

// K3 obsolete — 3-col (Scenario 4 + RoMSH 5 + Category 3) 레이아웃 폐기.
// (R1) 인하대 연구 충실도 회복: RoMSH binary 출력만 노출.
// ScenarioBadge(4-class) + DetectionCategoriesCard(카테고리) 제거.

interface ConclusionStripV2Props {
  analysis: Pick<
    Analysis,
    | "romsh_label"
    | "romsh_status"
    | "romsh_probability"
    | "romsh_threshold"
    | "romsh_model_version"
    | "romsh_error_message"
    | "romsh_temperature"
  >;
  /** features prop 유지 — RoMSHInputCard 등 외부 호출부 시그니처 보존 */
  features: (AnalysisFeature | FeatureData)[];
  /**
   * PLAN 2-9a: RoMSH 입력 성분 표('자세히'에서만, SHOW_ROMSH_INPUTS 뒤). 시연
   * 시나리오 모드에서는 부모가 넘기지 않는다. evidence 는 시나리오로 덮어쓴 값이
   * 아니라 원본 분석 상세의 것이다.
   */
  romshInput?: {
    evidence: AnalysisEvidence | null | undefined;
    selectedFeatureId: string | null;
    onSelectFeature: (featureId: string) => void;
  };
}

// ── RoMSH zone colors ────────────────────────────────────────────────────────

type Zone = "green" | "orange" | "red" | "gray";

// Zone은 zoneBadge + zoneMessage 텍스트로 충분 인코딩됨 (redundancy 제거)
// — 좌측 accent bar 폐기 (peripheral cue 대신 시각 무게 절약 우선)
const romshZoneCls: Record<Zone, string> = {
  green: "",
  orange: "",
  red: "",
  gray: "",
};

const zoneBadgeCls: Record<Zone, string> = {
  green: "bg-green-200 text-green-900",
  orange: "bg-orange-200 text-orange-900",
  red: "bg-red-200 text-red-900",
  gray: "bg-gray-200 text-gray-700",
};

/**
 * 정성 밴드 (S23-E): "방화/자연발화" 방향 제거.
 * 2026-09-23: 우리가 정한 확률 경계(0.5·0.3/0.7)로 가르던 것을 모델이 기록한
 * 판정(romsh_label)으로 바꿨다. 근거 docs/2026/260922_인하대_RoMSH_v3_검토_재현.md
 * §1-7·§8 결정 3 — 모델 임계값 0.951 과 화면 0.5 가 서로 다른 말을 하고 있었다.
 */
function getDecisionLabel(decision: RomshDecision): string {
  if (decision === "similar") return "모델 판정: 인화성 패턴 유사";
  if (decision === "not_similar") return "모델 판정: 유사 아님";
  return "모델 판정 없음";
}

/**
 * K6 OOD 고정 문구 — @/constants/romshOod 에서 import (단일 출처).
 * 확률 과신 방지를 위해 항상 표시.
 */
const OOD_WARNING = ROMSH_OOD_WARNING;

/**
 * ConclusionStripV2 — AI 보조 분석 지표 (연구용). S23-E 재프레이밍.
 *
 * 이전 역할: 상단 "방화/자연발화" 단정 결론 스트립.
 * 현재 역할: 하단 접이식 "AI 보조 분석 지표 (연구용)" — 인화성 물질 패턴 유사도 표시.
 *   - 기본(compact) 노출: 정성 밴드(높음/중간/낮음) + 중립 zone 메시지.
 *   - 상세(expanded): 원 확률 수치, 메타, OOD 경고 (거짓 정밀도 방지).
 *
 * 이전 레이아웃: 12-col → Scenario(4) | RoMSH(5) | Category(3)  [K3 obsolete]
 * 현재 레이아웃: RoMSH 단독 full-width (col-12)
 */
function ConclusionStripV2({
  analysis,
  features,
  romshInput,
}: ConclusionStripV2Props) {
  // RoMSH zone based on raw probability
  const prob = analysis.romsh_probability ?? null;
  const isNoMs = analysis.romsh_status === "not_applicable_no_ms";

  // compact 토글 — 기본 간략 (정성 밴드만). 상세에서 수치 노출.
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  // 모델이 기록한 판정(romsh_label)을 그대로 쓴다 — 화면이 다시 가르지 않는다.
  const decision: RomshDecision =
    analysis.romsh_label === 1
      ? "similar"
      : analysis.romsh_label === 0
        ? "not_similar"
        : "none";

  // iter2.1 A-1: 자연어 결론 derive (판정 메시지 + 우세 카테고리 요약)
  const naturalConclusion = deriveNaturalConclusion(
    decision,
    features as FeatureData[],
  );
  const romshZone: Zone =
    decision === "similar"
      ? "red"
      : decision === "not_similar"
        ? "green"
        : "gray";

  const romshMainLabel =
    isNoMs || decision === "none" ? null : getDecisionLabel(decision);

  return (
    <div data-testid="conclusion-strip-v2">
      {/* ── AI 보조 분석 지표 (연구용) full-width ──────────────────────────────── */}
      <div
        data-testid="romsh-card"
        className={panelCls("primary", `${romshZoneCls[romshZone]} space-y-2`)}
      >
        {/* 헤더 행: 출처 명확화 + compact 토글 버튼 */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {/* S23-E: "방화 의심도 모델" → "인화성 물질 패턴 유사도 (보조 연구지표)" */}
            <span className="text-xs font-black uppercase tracking-widest opacity-60">
              AI 보조 분석 지표 (연구용) — 인하대 RoMSH
            </span>
            <span
              role="img"
              aria-label={ROMSH_ABBR_TOOLTIP}
              title={ROMSH_ABBR_TOOLTIP}
              className="text-xs opacity-50 cursor-help"
            >
              ⓘ
            </span>
          </div>
          {/* compact 토글 */}
          <button
            type="button"
            aria-label={isExpanded ? "간략히 보기" : "자세히 보기"}
            onClick={() => setIsExpanded((v) => !v)}
            className="shrink-0 text-xs text-gray-400 hover:text-gray-600 transition-colors px-1.5 py-0.5 rounded hover:bg-gray-100"
          >
            {isExpanded ? "▾ 간략히" : "▸ 자세히"}
          </button>
        </div>

        {/* ── 항상 노출 (간략/자세히 공통) ── */}

        {/* 정성 밴드 (S23-E): 높음/중간/낮음 — 원 확률 수치는 상세/툴팁에만 */}
        <div className="flex items-center gap-2 flex-wrap">
          <span
            data-testid="zone-meaning-badge"
            title={ZONE_TOOLTIP}
            className={`text-xs font-semibold px-2 py-0.5 rounded cursor-help ${zoneBadgeCls[romshZone]}`}
          >
            {getDecisionLabel(decision)}
          </span>
        </div>

        {/* 간략 모드: 중립 zone 메시지 (방화/자연발화 단정 없음) */}
        {!isExpanded && decision !== "none" && (
          <p
            data-testid="natural-zone-message"
            className="text-base text-gray-800 leading-snug"
          >
            {naturalConclusion.zoneMessage}
          </p>
        )}

        {!isExpanded && isNoMs && (
          <p className="text-sm text-slate-700">
            RoMSH 산출 불가 — MS 미수집 시료. 동정·패턴 유사도 계산 불가 (계산
            불가이며 음성/0 아님).
          </p>
        )}

        {/* ── 자세히 모드 전용 ── */}
        {isExpanded && (
          <>
            {/* S23-E: 상세 레이블 + 원 확률 수치 (거짓 정밀도 방지: 상세에만 노출) */}
            {romshMainLabel && (
              <div className="flex items-baseline gap-3 flex-wrap pt-1">
                <span className="text-xl font-bold leading-tight text-slate-800">
                  {romshMainLabel}
                </span>
              </div>
            )}

            {/* iter2.1 A-1: 자연어 결론 2줄 */}
            {decision !== "none" && (
              <>
                <p
                  data-testid="natural-zone-message"
                  className="text-base text-gray-800 leading-snug"
                >
                  {naturalConclusion.zoneMessage}
                </p>
                <p
                  data-testid="natural-dominant-categories"
                  className="text-xs text-gray-600 leading-snug"
                >
                  {naturalConclusion.dominantCategoriesSummary}
                </p>
              </>
            )}

            {/* I1 (iter2 A-4): prob 메타 라인 분리 */}
            {prob != null && (
              <p
                data-testid="prob-meta-line"
                className="text-xs font-mono opacity-50"
              >
                prob {prob.toFixed(3)}
                {analysis.romsh_threshold != null &&
                  ` · thr ${analysis.romsh_threshold}`}
                {analysis.romsh_temperature != null &&
                  ` · temp ${analysis.romsh_temperature}`}
              </p>
            )}

            {/* I2: 모델 판정 footnote — 모델 임계값이 기록된 분석에만 */}
            {prob != null && analysis.romsh_threshold != null && (
              <p
                data-testid="binary-decision-footnote"
                className="text-xs opacity-50"
              >
                ↑ 모델 판정 (prob {prob.toFixed(3)}{" "}
                {decision === "similar" ? "≥" : "<"} 모델 임계값{" "}
                {analysis.romsh_threshold.toFixed(3)})
              </p>
            )}

            {analysis.romsh_status === "skipped_no_compounds" && (
              <p className="text-sm text-slate-700">
                RoMSH 분류 불가 — 검출 화합물 0건 또는 SMILES 정보 부재. 시료
                재측정 또는 분석 재실행 권장
              </p>
            )}
            {analysis.romsh_status === "not_applicable_no_ms" && (
              <p className="text-sm text-slate-700">
                RoMSH 산출 불가 — MS 미수집 시료. 동정·패턴 유사도 계산 불가
                (계산 불가이며 음성/0 아님).
              </p>
            )}
            {analysis.romsh_status === "model_unavailable" && (
              <p className="text-sm text-slate-700">
                RoMSH 모델 일시 미가용 — 시스템 관리자 문의
              </p>
            )}
            {analysis.romsh_status === "failed" && (
              <p className="text-sm text-slate-700">
                RoMSH 분석 실패
                {analysis.romsh_error_message
                  ? ` — ${analysis.romsh_error_message}`
                  : ""}
              </p>
            )}
            {!analysis.romsh_status && (
              <p className="text-sm text-gray-400">
                RoMSH 분석 미실행 — 재분석 필요
              </p>
            )}

            {/* PLAN 2-9a: RoMSH 입력 성분 — 상태 문구 다음, 모델 줄 앞 */}
            {SHOW_ROMSH_INPUTS && romshInput && (
              <RomshInputTable
                evidence={romshInput.evidence}
                romshStatus={analysis.romsh_status}
                features={features as AnalysisFeature[]}
                selectedFeatureId={romshInput.selectedFeatureId}
                onSelectFeature={romshInput.onSelectFeature}
              />
            )}

            {/* I1: 모델 라인 */}
            {analysis.romsh_model_version && (
              <p
                data-testid="model-version-line"
                className="text-xs opacity-60 font-mono border-t border-gray-200 pt-2"
              >
                모델: {analysis.romsh_model_version}
              </p>
            )}

            {/* A-2 (iter2): OOD demote */}
            <div
              data-testid="ood-warning"
              className="flex items-start gap-2 border-t border-gray-200 pt-2"
            >
              <span className="text-xs opacity-50 mt-0.5" aria-hidden="true">
                ⓘ
              </span>
              <p className="text-[11px] opacity-50 leading-snug">
                {OOD_WARNING}
              </p>
            </div>

            {/* S23-E caveat: 보조 연구지표 명시 */}
            <p className="text-[11px] text-gray-500 leading-snug border-t border-gray-200 pt-2">
              {AI_AUX_CAVEAT}
            </p>
          </>
        )}

        {/* 간략 모드에서도 status 오류 메시지는 노출 (정보 손실 방지) */}
        {!isExpanded && (
          <>
            {analysis.romsh_status === "skipped_no_compounds" && (
              <p className="text-sm text-slate-700">
                RoMSH 분류 불가 — 검출 화합물 0건 또는 SMILES 정보 부재. 시료
                재측정 또는 분석 재실행 권장
              </p>
            )}
            {analysis.romsh_status === "not_applicable_no_ms" && (
              <p className="text-sm text-slate-700">
                RoMSH 산출 불가 — MS 미수집 시료. 동정·패턴 유사도 계산 불가
                (계산 불가이며 음성/0 아님).
              </p>
            )}
            {analysis.romsh_status === "model_unavailable" && (
              <p className="text-sm text-slate-700">
                RoMSH 모델 일시 미가용 — 시스템 관리자 문의
              </p>
            )}
            {analysis.romsh_status === "failed" && (
              <p className="text-sm text-slate-700">
                RoMSH 분석 실패
                {analysis.romsh_error_message
                  ? ` — ${analysis.romsh_error_message}`
                  : ""}
              </p>
            )}
            {!analysis.romsh_status && (
              <p className="text-sm text-gray-400">
                RoMSH 분석 미실행 — 재분석 필요
              </p>
            )}
            {/* prob-meta-line: 간략 모드에서도 hidden하되 testid 보존 (aria-hidden) */}
            {prob != null && (
              <p
                data-testid="prob-meta-line"
                className="sr-only"
                aria-hidden="true"
              >
                prob {prob.toFixed(3)}
                {analysis.romsh_threshold != null &&
                  ` · thr ${analysis.romsh_threshold}`}
                {analysis.romsh_temperature != null &&
                  ` · temp ${analysis.romsh_temperature}`}
              </p>
            )}
            {/* binary-decision-footnote: 간략 모드 sr-only */}
            {prob != null && analysis.romsh_threshold != null && (
              <p
                data-testid="binary-decision-footnote"
                className="sr-only"
                aria-hidden="true"
              >
                ↑ 모델 판정 (prob {prob.toFixed(3)}{" "}
                {decision === "similar" ? "≥" : "<"} 모델 임계값{" "}
                {analysis.romsh_threshold.toFixed(3)})
              </p>
            )}
            {/* ood-warning: 간략 모드 sr-only */}
            <div
              data-testid="ood-warning"
              className="sr-only"
              aria-hidden="true"
            >
              <p>{OOD_WARNING}</p>
            </div>
            {/* model-version-line: 간략 모드 sr-only */}
            {analysis.romsh_model_version && (
              <p
                data-testid="model-version-line"
                className="sr-only"
                aria-hidden="true"
              >
                모델: {analysis.romsh_model_version}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default memo(ConclusionStripV2);
